import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '../analytics/use-analytics';
import { useDebugLogging } from '../utils';

import { Logout } from './auth-hooks';
import { useIsAlive, useTimeoutContext } from './timeout-context';
import { AuthenticationResponse, PostLogin } from './types';
import { useAuthCallbacks } from './use-auth-callbacks';
import { useOrganisation, useOrganisationValue } from './use-organisation';

const CHECK_INTERVAL_MINUTES = 5;
const SECONDS_PER_MINUTE = 60;
const MILLIS_PER_SECOND = 1000;
const CHECK_INTERVAL_MILLIS = CHECK_INTERVAL_MINUTES * SECONDS_PER_MINUTE * MILLIS_PER_SECOND;

type UseCheckAuthStatus = {
  apiUrl: string;
  setIsAuthenticated: (b: boolean) => void;
  postLogin: PostLogin;
  setIsLoading: (b: boolean) => void;
};

const useCheckAuthStatus = ({ apiUrl, postLogin, setIsLoading, setIsAuthenticated }: UseCheckAuthStatus) => {
  const [organisation] = useOrganisation();
  const analytics = useAnalytics();
  const addToLog = useDebugLogging();

  return useCallback(async () => {
    addToLog('check auth');
    try {
      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: 'GET',
        credentials: 'include',
      });
      const responsePayload = (await response.json()) as AuthenticationResponse;
      if (organisation && organisation !== responsePayload.organisation) {
        throw Error('Mismatched organisation');
      }

      if (responsePayload.success) {
        addToLog('auth check success');
        if (responsePayload.authSystemId) {
          analytics.identify({
            distinctId: responsePayload.authSystemId,
          });
        }

        if (responsePayload.organisation) {
          analytics.setGroup({
            groupType: 'organisation',
            groupKey: responsePayload.organisation,
            properties: {
              name: responsePayload.organisation,
            },
          });
        }

        postLogin(responsePayload);
      } else {
        addToLog('auth check fail');
        setIsAuthenticated(false);
      }
    } catch (err) {
      if (!(err instanceof TypeError && err.message === 'Failed to fetch')) {
        addToLog('auth error - logging out');
        setIsAuthenticated(false);
      } else {
        addToLog('auth error - not logging out - network failure');
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, setIsAuthenticated, postLogin, setIsLoading, organisation, analytics, addToLog]);
};

type UseDoAuthChecks = {
  checkAuthStatus: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
};

const useDoPeriodicAuthChecks = ({ checkAuthStatus, isAuthenticated, isLoading }: UseDoAuthChecks) => {
  const isAlive = useIsAlive();

  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      checkAuthStatus();
    }
  }, [checkAuthStatus, isAuthenticated, isLoading]);

  const clearPeriodicCheck = useCallback((intervalId: NodeJS.Timeout) => {
    clearInterval(intervalId);
    intervalRef.current = null;
  }, []);

  useEffect(() => {
    if (!isAlive && intervalRef.current) {
      clearPeriodicCheck(intervalRef.current);
    }
  }, [isAlive, clearPeriodicCheck]);

  useEffect(() => {
    const intervalId = intervalRef.current;
    if (isAuthenticated && intervalId === null) {
      intervalRef.current = setInterval(() => {
        checkAuthStatus();
      }, CHECK_INTERVAL_MILLIS);
    } else if (!isAuthenticated && intervalId !== null) {
      clearPeriodicCheck(intervalId);
    }

    return () => {
      if (intervalId !== null) {
        clearPeriodicCheck(intervalId);
      }
    };
  }, [isAuthenticated, checkAuthStatus, clearPeriodicCheck]);
};

type UseAuthArgs = {
  apiUrl: string;
  cognitoDomain: string;
  clientId?: string;
};

const useAuthState = (appClientId?: string) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string>();
  const [authSystemId, setAuthSystemId] = useState<string>();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticationResponse>();
  const [clientId, setClientId] = useState<string | undefined>(appClientId);
  const [isDefaultClientId, setIsDefaultClientId] = useState<boolean>(true);

  return {
    authError,
    authSystemId,
    authenticatedUser,
    clientId,
    isAuthenticated,
    isDefaultClientId,
    isLoading,
    setAuthError,
    setAuthSystemId,
    setAuthenticatedUser,
    setClientId,
    setIsAuthenticated,
    setIsDefaultClientId,
    setIsLoading,
  };
};

const useAutoTimeout = (logout: Logout, isAuthenticated: boolean) => {
  const { isAlive, setTrackingEnabled } = useTimeoutContext();
  const addToLog = useDebugLogging();

  useEffect(() => {
    setTrackingEnabled(isAuthenticated);
  }, [isAuthenticated, setTrackingEnabled]);

  useEffect(() => {
    if (!isAlive && isAuthenticated) {
      addToLog('authenticated and inactive - log out');
      logout();
    }
  }, [isAlive, logout, isAuthenticated, addToLog]);
};

const useAuth = ({ apiUrl, clientId: appClientId, cognitoDomain }: UseAuthArgs) => {
  const authState = useAuthState(appClientId);
  const organisation = useOrganisationValue();

  const authCallbacks = useAuthCallbacks({
    apiUrl,
    clientId: authState.clientId,
    cognitoDomain,
    setAuthError: authState.setAuthError,
    setAuthSystemId: authState.setAuthSystemId,
    setAuthenticatedUser: authState.setAuthenticatedUser,
    setClientId: authState.setClientId,
    setIsAuthenticated: authState.setIsAuthenticated,
    setIsDefaultClientId: authState.setIsDefaultClientId,
  });

  const checkAuthStatus = useCheckAuthStatus({
    apiUrl,
    postLogin: authCallbacks.postLogin,
    setIsAuthenticated: authState.setIsAuthenticated,
    setIsLoading: authState.setIsLoading,
  });

  useAutoTimeout(authCallbacks.logout, authState.isAuthenticated);

  useDoPeriodicAuthChecks({
    checkAuthStatus,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
  });

  return {
    authError: authState.authError,
    authSystemId: authState.authSystemId,
    authenticatedUser: authState.authenticatedUser,
    checkAuthStatus,
    getOrgSettings: authCallbacks.getOrgSettings,
    handleCallback: authCallbacks.handleCallback,
    initiateChangePassword: authCallbacks.initiateChangePassword,
    initiateHostedLogin: authCallbacks.initiateHostedLogin,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    logout: authCallbacks.logout,
    organisation,
    postLogin: authCallbacks.postLogin,
  };
};

export { useAuth };
